import styled from 'styled-components'

import { Button } from '@/components/ui/button'
import { QuantityBadge } from '@/components/ui/quantity-badge'

// Note: nudges the button right so it lines up with the plain-text/chip
// values in the rest of the column, which have no inline padding of their own.
export const FlushFieldButton = styled(Button)(() => ({
  marginLeft: '1rem',
}))

export const FieldRow = styled.div(({ theme }) => ({
  '&:last-child': {
    borderBottom: 'none',
  },
  alignItems: 'center',
  borderBottom: `${theme.border.size['1']} solid ${theme.colors.gray400}`,
  display: 'grid',
  gap: theme.space['24'],
  gridTemplateColumns: '20rem minmax(0, 1fr)',
  minHeight: '4.8rem',
  // Note: 2px subtracted vertically to account for input border (1px each side) + FieldRow borderBottom (1px)
  padding: `calc(${theme.space['8']} - 2px) ${theme.space['16']}`,
}))

export const PhotoRow = styled(FieldRow)(() => ({
  paddingBottom: '0.8rem',
  paddingTop: '0.8rem',
}))

export const LabelWrapper = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['6'],
}))

interface FieldValueProps {
  $isViewOnly?: boolean
}

export const FieldValue = styled.span<FieldValueProps>(({ theme, $isViewOnly }) => ({
  alignItems: 'center',
  color: theme.colors.black800,
  display: 'flex',
  flexWrap: 'wrap',
  fontSize: '1.4rem',
  gap: theme.space['8'],
  ...($isViewOnly && { paddingLeft: theme.space['10'] }),
}))

export const PmsIconWrapper = styled.span(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.blue100,
  borderRadius: theme.border.radius['circle'],
  color: theme.colors.blue500,
  cursor: 'help',
  display: 'inline-flex',
  flexShrink: 0,
  height: '2.4rem',
  justifyContent: 'center',
  width: '2.4rem',
}))

export const FieldInputWrapper = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  gap: theme.space['8'],
  minWidth: 0,
}))

export const FieldActions = styled.div(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  gap: theme.space['4'],
}))

export const ActionButton = styled.button(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.colors.gray100,
    color: theme.colors.black800,
  },
  alignItems: 'center',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: theme.border.radius['4'],
  color: theme.colors.gray500,
  cursor: 'pointer',
  display: 'inline-flex',
  height: '2.8rem',
  justifyContent: 'center',
  transition: `color ${theme.transition.duration.sm} ease, background-color ${theme.transition.duration.sm} ease`,

  width: '2.8rem',
}))

export const PhotoPlaceholder = styled.div(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.gray200,
  borderRadius: theme.border.radius['12'],
  color: theme.colors.gray500,
  display: 'flex',
  flexShrink: 0,
  fontSize: '2rem',
  fontWeight: 600,
  height: '10rem',
  justifyContent: 'center',
  width: '10rem',
}))

export const DatePickerWrapper = styled.div(() => ({
  flex: 1,
  minWidth: 0,
}))

export const TagsWrapper = styled.div<{ $hasOverflowBadge?: boolean }>(
  ({ theme, $hasOverflowBadge }) => ({
    display: 'flex',
    // Fills the rest of the row so the absolutely-positioned overflow badge's
    // `right: 0` pins to the actual right edge of the field, not just the edge
    // of the tags' own shrink-wrapped content.
    flex: 1,
    flexWrap: 'wrap',
    gap: theme.space['6'],
    // The overflow badge is absolutely positioned and vertically centered against
    // this box's full (possibly multi-line) height, so it no longer takes up space
    // in the wrap itself — reserve room here instead, so tags never sit under it.
    paddingRight: $hasOverflowBadge ? theme.space['48'] : 0,
    position: 'relative',
  }),
)

// QuantityBadge defaults to `margin-left: auto` and normal flex flow, which suits
// its other use (pinned to the right of a single-line Select trigger). Here it
// needs to sit centered against a box whose height changes as tags wrap, so it's
// taken out of flow entirely and centered via absolute positioning + transform.
// Sizing is overridden to match the small Badge pills it sits alongside, instead
// of QuantityBadge's own (shorter) default height/padding.
export const TagsQuantityBadge = styled(QuantityBadge)(({ theme }) => ({
  height: 'auto',
  marginLeft: 0,
  minWidth: 'auto',
  padding: `${theme.space['2']} ${theme.space['6']}`,
  position: 'absolute',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
}))
